<template>
    <NodeViewWrapper ref="customTagsAndFieldsTrigger" as="span" :hash="hash">
        <span
            :hash="hash"
            :class="classes"
            :draggable="isDraggable"
            data-drag-handle
            @mouseover="handleMouseOver"
            @mouseleave="handleMouseLeave"
            @focus="handleFocus"
            @blur="handleBlur"
            @dragstart="handleDragStart"
            @dragend="handleDragEnd"
        >
            <CompassIcon v-if="showDragIcon" icon="ai-drag-plan" size="extra-small" />
            <CompassIcon
                v-if="type === 'ai'"
                icon="ai-sparkles-simple"
                class="custom-tags-and-fields__ai-icon"
            />
            <span class="typographies-paragraph-small-semibold">
                {{ label }}
            </span>
        </span>
        <CompassPopover
            :isVisible="showAIBlockPopover"
            placement="bottom-start"
            customClass="rich-text-style custom-tags-and-fields__popover"
            triggerRef="customTagsAndFieldsTrigger"
            @close="hideAIBlockPopover"
            @mouseleave="handleMouseLeave"
        >
            <div class="custom-tags-and-fields__popover-header">
                <span class="typographies-paragraph-small-bold">
                    {{ $t.__('richtext.CustomTagsAndFields.popover_title_ai') }}
                </span>
                <CompassIcon icon="ai-sparkles-simple" class="custom-tags-and-fields__ai-icon" />
            </div>
            <div
                class="custom-tags-and-fields__popover-content rich-text-style"
                v-html="replaceTagsWithSpan(prompt)"
            />
            <div v-if="isDraggable" class="custom-tags-and-fields__popover-footer">
                <ButtonAI
                    :label="$t.__('common.labels.edit')"
                    type="secondary"
                    leftIcon="edit"
                    @click="handleEdit"
                />
            </div>
        </CompassPopover>
    </NodeViewWrapper>
</template>

<script>
import { NodeViewWrapper, nodeViewProps } from '@tiptap/vue-2';
import ButtonAI from 'vcomponents/common/ai/ButtonAI.vue';

export default {
    components: {
        NodeViewWrapper,
        ButtonAI,
    },
    props: {
        // (Tiptap) used to pass the nodeView props to the NodeViewWrapper (CustomTagFields.js)
        // eslint-disable-next-line vue/no-unused-properties
        nodeViewProps,
    },
    data() {
        return {
            showAIBlockPopover: false,
            tags: [],
        };
    },
    computed: {
        type() {
            return this.node.attrs.type;
        },
        label() {
            return this.node.attrs.label;
        },
        hash() {
            return this.node.attrs.hash;
        },
        prompt() {
            return this.replaceSpanWithTag(this.node.attrs.prompt);
        },
        isDraggable() {
            if (this.isDisabled) {
                return false;
            }
            return this.node.attrs.isDraggable;
        },
        isHovered() {
            return this.node.attrs.isHovered;
        },
        isFocused() {
            return this.node.attrs.isFocused;
        },
        isDisabled() {
            return this.node.attrs.isDisabled;
        },
        showDragIcon() {
            return (this.isHovered || this.isFocused) && this.isDraggable;
        },
        classes() {
            // style is on "__richtext.scss" (styleguide)
            return [
                'custom-tags-and-fields',
                `custom-tags-and-fields__${this.type}`,
                this.isHovered && this.isDraggable
                    ? `custom-tags-and-fields__${this.type}--hovered`
                    : '',
                this.isFocused && this.isDraggable
                    ? `custom-tags-and-fields__${this.type}--focused`
                    : '',
            ];
        },
    },
    created() {
        EventBus.on('customTagsAI:updateTagAI', this.updateTagAI);
    },
    destroyed() {
        EventBus.off('customTagsAI:updateTagAI', this.updateTagAI);
    },
    mounted() {
        // update the node attributes (props from CustomTagAI.js)
        this.updateNodeAttributes({
            type: this.type,
            label: this.label,
            prompt: this.prompt,
            hash: this.hash,
            isDraggable: this.isDraggable,
            isHovered: this.isHovered,
            isFocused: this.isFocused,
            isDisabled: this.isDisabled,
        });
        // get the data "tags" from the parent component (avoids calling API getTags for each tag)
        let parentComponent = this.$refs.customTagsAndFieldsTrigger.$parent;
        for (let i = 0; i < 5; i++) {
            if (parentComponent.$parent) {
                parentComponent = parentComponent.$parent;
            } else {
                break;
            }
        }
        this.tags = parentComponent.tags;
    },
    methods: {
        replaceTagsWithSpan(message) {
            for (const tag of this.tags) {
                const regex = new RegExp(tag.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
                message = message.replace(
                    regex,
                    `<span class="custom-tags-and-fields custom-tags-and-fields__default typographies-paragraph-small-semibold" tag="${tag.key}">${tag.name}</span>`,
                );
            }
            return message;
        },
        replaceSpanWithTag(content) {
            if (!this.tags) {
                return content;
            }

            const regex =
                /<span [^>]*tag="([^"]+)"[^>]*class="[^"]*custom-tags-and-fields[^"]*"[^>]*>[^<]+<\/span>/g;

            return content.replace(regex, (match, tag) => {
                const tagObj = this.tags.find((t) => t.key === tag);
                return tagObj ? tagObj.key : match;
            });
        },
        handleDragStart() {
            this.showAIBlockPopover = false;
            this.$emit('close');
        },
        handleMouseOver() {
            if (!this.isDraggable) {
                return;
            }
            this.showAIBlockPopover = this.type === 'ai';
            this.updateNodeAttributes({ isHovered: true });
        },
        handleDragEnd(event) {
            // if the drag end event is on richtext without extension CustomTagsAI, cancel drop event
            if (!event.relatedTarget?.closest('.custom-tags-and-fields__popover')) {
                this.showAIBlockPopover = false;
                this.$emit('close');
            }
        },
        handleMouseLeave(event) {
            if (!event.relatedTarget?.closest('.custom-tags-and-fields__popover')) {
                this.hideAIBlockPopover();
            }
        },
        handleFocus() {
            if (!this.isDraggable) {
                return;
            }
            this.updateNodeAttributes({ isFocused: true });
        },
        handleBlur() {
            this.hideAIBlockPopover();
        },
        updateNodeAttributes(attrs) {
            if (this.isDisabled) {
                return;
            }
            this.updateAttributes(attrs);
        },
        hideAIBlockPopover() {
            this.showAIBlockPopover = false;
            this.$emit('close');
            this.updateNodeAttributes({ isHovered: false, isFocused: false });
        },
        handleEdit() {
            this.hideAIBlockPopover();
            const data = {
                hash: this.hash,
                label: this.label,
                prompt: this.replaceTagsWithSpan(this.prompt),
            };
            EventBus.emit('customTagsAndFieldsRichText:editTagAI', data);
        },
        // eslint-disable-next-line vue/no-unused-properties
        updateTagAI(option) {
            const optionTag = option;
            if (optionTag.hash === this.hash) {
                this.updateNodeAttributes({
                    label: optionTag.label,
                    prompt: this.replaceSpanWithTag(optionTag.prompt),
                });
                EventBus.emit('customTagsAndFieldsRichText:closeModal');
            }
        },
    },
};
</script>

<style lang="scss">
.compass-popover__container {
    &.rich-text-style {
        &.custom-tags-and-fields__popover {
            .compass-popover__content {
                background: var(--colors-sellsy-ai-05);
            }
        }
    }
}
</style>
